import React, { memo } from 'react';
import { Box, Paper, Stack, Tabs, Tab, Typography, TextField, Button } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';

/** 프롬프트 관련 필드 라벨 */
const PROMPT_FIELD_LABELS = {
  baseSystemPrompt: '기본 시스템 프롬프트',
  problemTitle: '문제 제목',
  problemDescription: '문제 설명',
  promptEvaluationCriteria: '평가 기준',
  promptFewShot: 'Few-shot 예시',
};

const promptPaperSx = {
  p: 2,
  bgcolor: 'grey.50',
  maxHeight: 300,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: '0.875rem',
};

/** baseSystemPrompt 내 {evaluationCriteria}, {title}, {description}, {fewShot} 자리 치환 */
const resolvePromptPlaceholders = (prompt) => {
  const base = prompt.baseSystemPrompt ?? prompt.systemPrompt ?? '';
  if (!base) return '';
  const evaluationCriteria = prompt.promptEvaluationCriteria ?? prompt.judgmentCriteria ?? '';
  const title = prompt.problemTitle ?? '';
  const description = prompt.problemDescription ?? '';
  const fewShot = prompt.promptFewShot ?? prompt.fewShotExamples ?? '';
  return base
    .replace(/\{evaluationCriteria\}/g, evaluationCriteria)
    .replace(/\{title\}/g, title)
    .replace(/\{description\}/g, description)
    .replace(/\{fewShot\}/g, fewShot);
};

/**
 * 프롬프트 탭 패널 - 읽기 전용 (상세보기)
 */
export const PromptTabsView = memo(({ prompts = [], tabLabelFormat = (idx) => `${idx + 1}차 프롬프트` }) => {
  const [tab, setTab] = React.useState(0);
  const sortedPrompts = React.useMemo(
    () => [...prompts].sort((a, b) => (a.attemptOrder ?? a.id) - (b.attemptOrder ?? b.id)),
    [prompts]
  );

  if (sortedPrompts.length === 0) return null;

  return (
    <Paper sx={{ mb: 3, overflow: 'hidden' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {sortedPrompts.map((prompt, idx) => (
          <Tab key={prompt.id ?? idx} label={tabLabelFormat(idx)} />
        ))}
      </Tabs>
      {sortedPrompts.map((prompt, idx) => (
        <Box
          key={prompt.id ?? idx}
          role="tabpanel"
          hidden={tab !== idx}
          sx={{ p: 2 }}
        >
          <Stack spacing={2}>
            <Stack direction="row" gap={2} flexWrap="wrap">
              <Typography variant="body2">
                <strong>모델:</strong> {prompt.model ?? '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Temperature:</strong> {prompt.temperature ?? '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Max Tokens:</strong> {prompt.maxTokens ?? '-'}
              </Typography>
            </Stack>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                프롬프트
              </Typography>
              <Paper variant="outlined" sx={promptPaperSx}>
                {resolvePromptPlaceholders(prompt)}
              </Paper>
            </Box>
          </Stack>
        </Box>
      ))}
    </Paper>
  );
});

/**
 * 프롬프트 탭 패널 - 편집 모드
 */
export const PromptTabsEdit = memo(({
  prompts = [],
  tabLabelFormat = (idx) => `${idx + 1}차 프롬프트`,
  onUpdatePrompt,
  renderExtraHeader,
  handleApplyFirstToSecond,
}) => {
  const [tab, setTab] = React.useState(0);
  const sortedPrompts = React.useMemo(
    () => [...prompts].sort((a, b) => (a.attemptOrder ?? a.id) - (b.attemptOrder ?? b.id)),
    [prompts]
  );

  const applyButtonDisabled = React.useMemo(() => {
    if (sortedPrompts.length < 2 || !handleApplyFirstToSecond) return true;
    const first = sortedPrompts[0];
    const second = sortedPrompts[1];
    const getCriteria = (p) => (p?.promptEvaluationCriteria ?? p?.judgmentCriteria ?? '').trim();
    const getFewShot = (p) => (p?.promptFewShot ?? p?.fewShotExamples ?? '').trim();
    const criteriaSame = getCriteria(first) === getCriteria(second);
    const fewShotSame = getFewShot(first) === getFewShot(second);
    return criteriaSame && fewShotSame;
  }, [sortedPrompts, handleApplyFirstToSecond]);

  if (sortedPrompts.length === 0) return null;

  return (
    <Paper sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', gap: 1, flexWrap: 'wrap' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 48, flex: 1 }}
        >
          {sortedPrompts.map((prompt, idx) => (
            <Tab key={prompt.id ?? idx} label={tabLabelFormat(idx)} />
          ))}
        </Tabs>
        {renderExtraHeader?.()}
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopyIcon />}
          disabled={applyButtonDisabled}
          onClick={handleApplyFirstToSecond}
          sx={{ m: 1 }}
        >
          1차 프롬프트 내용을 2차 프롬프트에도 적용
        </Button>
      </Box>
      {sortedPrompts.map((prompt, idx) => (
        <Box
          key={prompt.id ?? idx}
          role="tabpanel"
          hidden={tab !== idx}
          sx={{ p: 2 }}
        >
          <Stack spacing={2}>
            <Stack direction="row" gap={2} flexWrap="wrap">
              <TextField
                label="모델"
                size="small"
                value={prompt.model ?? ''}
                onChange={(e) => onUpdatePrompt(idx, 'model', e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="Temperature"
                type="number"
                size="small"
                value={prompt.temperature ?? ''}
                onChange={(e) =>
                  onUpdatePrompt(idx, 'temperature', parseFloat(e.target.value) ?? '')
                }
                sx={{ minWidth: 100 }}
              />
              <TextField
                label="Max Tokens"
                type="number"
                size="small"
                value={prompt.maxTokens ?? ''}
                onChange={(e) =>
                  onUpdatePrompt(idx, 'maxTokens', parseInt(e.target.value, 10) || '')
                }
                sx={{ minWidth: 100 }}
              />
            </Stack>
            <Stack spacing={2}>
              <TextField
                label={PROMPT_FIELD_LABELS.promptEvaluationCriteria}
                fullWidth
                multiline
                rows={8}
                value={prompt.promptEvaluationCriteria ?? prompt.judgmentCriteria ?? ''}
                onChange={(e) => onUpdatePrompt(idx, 'promptEvaluationCriteria', e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'Consolas, "Courier New", monospace', fontSize: '0.875rem' } }}
              />
              <TextField
                label={PROMPT_FIELD_LABELS.promptFewShot}
                fullWidth
                multiline
                rows={15}
                value={prompt.promptFewShot ?? prompt.fewShotExamples ?? ''}
                onChange={(e) => onUpdatePrompt(idx, 'promptFewShot', e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'Consolas, "Courier New", monospace', fontSize: '0.875rem' } }}
              />
            </Stack>
          </Stack>
        </Box>
      ))}
    </Paper>
  );
});
